import { isEqual } from 'lodash';

/**
 * Lookup table of layout-switch rules per keyboard family.
 * Each entry defines which keys trigger shift/alt toggling
 * and how to retrieve the relevant layout pair from allLayouts.
 */
const LAYOUT_SWITCH_RULES = {
  text: {
    shiftKeys: new Set(['{shift}', '{lock}']),
    altKeys:   new Set(['{alt}', '{default}']),
    getShiftPair: l => [l.text.default, l.text.shift],
    getAltPair:   l => [l.text.default, l.text.alt],
  },
  textWithoutNumeric: {
    shiftKeys: new Set(['{shift}', '{lock}']),
    altKeys:   new Set(['{alt}', '{default}']),
    getShiftPair: l => [l.textWithoutNumeric.default, l.textWithoutNumeric.shift],
    getAltPair:   l => [l.textWithoutNumeric.default, l.textWithoutNumeric.alt],
  },
  textWithNumericEmail: {
    shiftKeys: new Set(['{special}', '{lowercase}']),
    altKeys:   new Set(['{special}', '{lowercase}']),
    getShiftPair: l => [l.textWithNumericEmail.default, l.textWithNumericEmail.alt],
    getAltPair:   l => [l.textWithNumericEmail.default, l.textWithNumericEmail.alt],
  },
  emailText: {
    shiftKeys: new Set(['{shift}', '{lock}']),
    altKeys:   new Set(['{alt}', '{default}']),
    getShiftPair: l => [l.emailText.default, l.emailText.shift],
    getAltPair:   l => [l.emailText.default, l.emailText.alt],
  },
  surveyText: {
    shiftKeys: new Set(['{shift}']),
    altKeys:   new Set(['{alt}', '{default}']),
    getShiftPair: l => [l.surveyText.default, l.surveyText.shift],
    getAltPair:   l => [l.surveyText.default, l.surveyText.alt],
  },
  textWithOutDone: {
    shiftKeys: new Set(['{shift}', '{lock}']),
    altKeys:   new Set(['{alt}', '{default}']),
    getShiftPair: l => [l.textWithOutDone.default, l.textWithOutDone.shift],
    getAltPair:   l => [l.textWithOutDone.default, l.textWithOutDone.alt],
  },
  textDefaultCaps: {
    shiftKeys: new Set(['{shift}', '{lock}']),
    altKeys:   new Set(['{alt}', '{default}']),
    getShiftPair: l => [l.textDefaultCaps.default, l.textDefaultCaps.shift],
    getAltPair:   l => [l.textDefaultCaps.default, l.textDefaultCaps.alt],
  },
};

/**
 * Detect which keyboard family the current layout belongs to,
 * by checking all known variant arrays via deep equality.
 */
function detectFamily(currentLayout, allLayouts) {
  const families = [
    'text', 'textWithoutNumeric', 'textWithNumericEmail',
    'emailText', 'surveyText', 'textWithOutDone', 'textDefaultCaps',
  ];
  for (const family of families) {
    const variants = allLayouts[family];
    if (!variants) continue;
    for (const variant of Object.values(variants)) {
      if (isEqual(currentLayout, variant)) return family;
    }
  }
  return null;
}

/**
 * Given a key press, determine the next layout to switch to.
 *
 * Returns the next layout array if this key is a layout-switch key,
 * or null if the key should be handled by the regular onclick handler.
 *
 * @param {string} key            — the key token pressed e.g. '{shift}'
 * @param {Array}  currentLayout  — the current active layout row array
 * @param {object} allLayouts     — full allLayouts object from buildLayouts()
 * @param {string} type           — the keyboard type prop (used as a hint)
 * @returns {Array|null}
 */
export function getNextLayout(key, currentLayout, allLayouts, type) {
  // Resolve family: prefer the type prop as a direct hint, fall back to detection
  const family = LAYOUT_SWITCH_RULES[type] ? type : detectFamily(currentLayout, allLayouts);
  const rules = LAYOUT_SWITCH_RULES[family];
  if (!rules) return null;

  if (rules.shiftKeys.has(key)) {
    const [defaultLayout, shiftLayout] = rules.getShiftPair(allLayouts);
    // textWithNumericEmail uses same key for shift/alt, treat as toggle between default and alt
    return isEqual(currentLayout, shiftLayout) ? defaultLayout : shiftLayout;
  }

  if (rules.altKeys.has(key) && !rules.shiftKeys.has(key)) {
    const [defaultLayout, altLayout] = rules.getAltPair(allLayouts);
    return isEqual(currentLayout, altLayout) ? defaultLayout : altLayout;
  }

  return null;
}
